import {
    createTeamInvite,
    getTeam,
    revokeTeamInvite,
    type PendingTeamInvite,
    type TeamMember,
} from '@api/teamInvite';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';

import InviteUserModal from './InviteUserModal';
import styles from './UsersTab.module.css';

const TEAM_QUERY_KEY = ['settings-team'];

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const fullName = (row: { firstName: string | null; lastName: string | null }) =>
    [row.firstName, row.lastName].filter(Boolean).join(' ') || '—';

const ROLE_LABEL: Record<string, string> = { ADMIN: 'Admin', USER: 'User' };

/** How many whole days a live invite has left; the row says "expired" past zero. */
const daysLeft = (iso: string) =>
    Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

type MemberRow = TeamMember & { name: string; roleLabel: string; isYou: boolean };

type PendingRow = PendingTeamInvite & { name: string };

/**
 * Settings › Users. Who has a login on this brand, who has been invited and has not yet
 * arrived, and the button that invites the next person.
 *
 * ADMINS ONLY, twice: the section is filtered out of the sidebar for anyone else, and every
 * route it calls refuses a non-admin session. Until this screen existed, every extra admin
 * on a brand was a hand-run script.
 *
 * INVITE, RESEND AND REVOKE, deliberately nothing more. There is no remove-user here yet:
 * other tables point at user ids and users carry no active flag, so taking a login away is
 * its own decision with its own design, not a trash icon on this grid.
 */
const UsersTab: FC = () => {
    const { currentUser } = useCurrentUser();
    const { showToastSuccess, showToastError } = useToaster();
    const [inviteOpen, setInviteOpen] = useState(false);
    const [busyInviteId, setBusyInviteId] = useState<string | null>(null);

    const teamQuery = useQuery({ queryKey: TEAM_QUERY_KEY, queryFn: getTeam });

    const members = useMemo<MemberRow[]>(
        () =>
            (teamQuery.data?.members ?? []).map((m) => ({
                ...m,
                name: fullName(m),
                roleLabel: ROLE_LABEL[m.role] ?? m.role,
                isYou: m.userId === currentUser?.id,
            })),
        [teamQuery.data?.members, currentUser?.id],
    );

    const pending = useMemo<PendingRow[]>(
        () => (teamQuery.data?.pendingInvites ?? []).map((p) => ({ ...p, name: fullName(p) })),
        [teamQuery.data?.pendingInvites],
    );

    const resend = async (invite: PendingTeamInvite) => {
        setBusyInviteId(invite.inviteId);
        try {
            const issued = await createTeamInvite({
                email: invite.email,
                firstName: invite.firstName ?? undefined,
                lastName: invite.lastName ?? undefined,
            });
            if (issued.emailed) {
                showToastSuccess({ title: `Re-sent the invite to ${issued.email}` });
            } else {
                showToastError({
                    title: 'The invite was refreshed but the email could not be sent',
                    subtitle: 'Open "Invite user" and send to the same address to get a link you can copy.',
                });
            }
            await teamQuery.refetch();
        } catch (err: any) {
            showToastError({
                title: 'Could not resend the invite',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setBusyInviteId(null);
        }
    };

    const revoke = async (invite: PendingTeamInvite) => {
        setBusyInviteId(invite.inviteId);
        try {
            await revokeTeamInvite(invite.inviteId);
            showToastSuccess({ title: `Withdrew the invite for ${invite.email}` });
            await teamQuery.refetch();
        } catch (err: any) {
            showToastError({
                title: 'Could not withdraw the invite',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setBusyInviteId(null);
        }
    };

    const memberColumns = useMemo<DataSheetColumn<MemberRow>[]>(
        () => [
            {
                key: 'name',
                header: 'Name',
                width: 'fit',
                render: (value: string, row) => (
                    <Group gap={6} wrap="nowrap">
                        <span>{value}</span>
                        {row.isYou && (
                            <Badge size="xs" variant="light" color="gray">
                                you
                            </Badge>
                        )}
                    </Group>
                ),
            },
            { key: 'email', header: 'Email', width: 'fit', render: (v: string | null) => v ?? '—' },
            { key: 'roleLabel', header: 'Role', width: 100 },
            { key: 'joinedAt', header: 'Joined', width: 130, render: formatDate },
        ],
        [],
    );

    const pendingColumns = useMemo<DataSheetColumn<PendingRow>[]>(
        () => [
            { key: 'email', header: 'Email', width: 'fit' },
            { key: 'name', header: 'Name', width: 'fit' },
            {
                key: 'invitedBy',
                header: 'Invited by',
                width: 'fit',
                render: (v: string | null) => v ?? '—',
            },
            {
                key: 'expiresAt',
                header: 'Status',
                width: 150,
                render: (value: string, row) =>
                    row.expired ? (
                        <Badge size="xs" variant="light" color="red">
                            Link expired
                        </Badge>
                    ) : (
                        <Badge size="xs" variant="light" color="blue">
                            {`Invited · ${daysLeft(value)} days left`}
                        </Badge>
                    ),
            },
            {
                key: 'inviteId',
                header: '',
                width: 170,
                align: 'right',
                render: (_: string, row) => (
                    <Group gap={6} justify="flex-end" wrap="nowrap">
                        <Button
                            size="compact-xs"
                            variant={ButtonVariant.SUBTLE}
                            loading={busyInviteId === row.inviteId}
                            onClick={() => void resend(row)}
                        >
                            Resend
                        </Button>
                        <Button
                            size="compact-xs"
                            variant={ButtonVariant.SUBTLE}
                            color="red"
                            disabled={busyInviteId === row.inviteId}
                            onClick={() => void revoke(row)}
                        >
                            Revoke
                        </Button>
                    </Group>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [busyInviteId],
    );

    return (
        <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
                <Stack gap={2}>
                    <Text className={styles.sectionTitle}>Users</Text>
                    <Text className={styles.sectionHint}>
                        Everyone with a login on this brand. Invite a colleague by email; they
                        choose their own password and join as an admin.
                    </Text>
                </Stack>
                <Button size="xs" onClick={() => setInviteOpen(true)}>
                    Invite user
                </Button>
            </Group>

            <DataSheet<MemberRow>
                data={members}
                columns={memberColumns}
                isLoading={teamQuery.isLoading}
                getRowKey={(row) => row.userId}
                emptyState="Nobody has a login on this brand yet."
                bare
                hideHeaderBar
            />

            <Stack gap="xs">
                <Text className={styles.sectionTitle}>Pending invites</Text>
                {!teamQuery.isLoading && pending.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No invites waiting. Anyone you invite shows here until they set up
                        their login.
                    </Text>
                ) : (
                    <DataSheet<PendingRow>
                        data={pending}
                        columns={pendingColumns}
                        isLoading={teamQuery.isLoading}
                        getRowKey={(row) => row.inviteId}
                        emptyState="No invites waiting."
                        bare
                        hideHeaderBar
                    />
                )}
            </Stack>

            <InviteUserModal
                opened={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onSent={() => void teamQuery.refetch()}
            />
        </Stack>
    );
};

export default UsersTab;
